import { Injectable, OnDestroy } from '@angular/core';
import { Store } from '@ngrx/store';
import { combineLatest, Observable, ReplaySubject, Subject } from 'rxjs';
import { map, take, takeUntil } from 'rxjs/operators';
import { EntityType, UserRole } from 'src/graphql/generated/graphql.generated';
import { AccessibleItemAuthorizationService } from 'src/lib/tcs-authorization/accessible-item-authorization.service';
import { CommCenterAuthorizationService } from 'src/lib/tcs-authorization/comm-center-authorization.service';
import { ControlZoneAuthorizationService } from 'src/lib/tcs-authorization/control-zone-authorization.service';
import { UserViewModel } from 'src/models/person-view-model';
import { AppState } from 'src/store/appstate';
import {
  CompanyProps,
  selectAllCompanyEntities,
} from 'src/store/generated/company-record.generated';
import { firstValueOrDefault } from 'src/util/rxjs';
import { OosAuthorizationService } from 'src/lib/tcs-authorization/oos-authorization.service';
import { UserService } from '../../lib/tcs-identity/user.service';

@Injectable({ providedIn: 'root' })
export class CompanyAuthorizationService implements OnDestroy {
  private _onDestroy$ = new Subject();
  public _authorizedCompanies$ = new ReplaySubject<CompanyProps[]>(1);
  public get authorizedCompanies$(): Observable<CompanyProps[]> {
    return this._authorizedCompanies$.asObservable();
  }

  get isAllCompaniesAuthorized(): boolean {
    const user = this._userService.user;
    return user != null && this.isAuthorizedForAllCompanies(user);
  }

  public isAuthorizedForAllCompanies(user: UserViewModel): boolean {
    return user.hasRoleAnyScope(UserRole.Developer) || user.allCompaniesAccessible === true;
  }

  public isAuthorizedForCompany$(
    company: { id: string } | { companyCode: string },
  ): Observable<boolean> {
    return this.authorizedCompanies$.pipe(
      map((companies) => {
        if ('id' in company) {
          return companies.find((c) => c.id === company.id) != null;
        }
        return companies.find((c) => c.companyCode === company.companyCode) != null;
      }),
    );
  }

  public isAuthorizedForCompany(
    company: { id: string } | { companyCode: string },
  ): Promise<boolean> {
    return firstValueOrDefault(
      this.isAuthorizedForCompany$(company).pipe(take(1), takeUntil(this._onDestroy$)),
      false,
    );
  }

  constructor(
    private _userService: UserService,
    private _commCenterAuthorization: CommCenterAuthorizationService,
    private _controlZoneAuthorization: ControlZoneAuthorizationService,
    private _accessibleItems: AccessibleItemAuthorizationService,
    private _oosAuthorization: OosAuthorizationService,
    store: Store<AppState>,
  ) {
    const authorizedCommCenters$ = this._commCenterAuthorization.getAuthorizedCommCenters();
    const authorizedControlZones$ = this._controlZoneAuthorization.getAuthorizedControlZones();
    const accessibleCompanies$ = this._accessibleItems.getAccessibleItems(EntityType.Company);
    const companies$ = store.select(selectAllCompanyEntities);
    const authorizedOosCommCenters$ = this._oosAuthorization.getAuthorizedCommCenters();

    const authorizedCompanies$: Observable<CompanyProps[]> = combineLatest([
      this._userService.user$,
      authorizedCommCenters$,
      authorizedControlZones$,
      accessibleCompanies$,
      companies$,
      authorizedOosCommCenters$,
    ]).pipe(
      map(([user, commCenters, controlZones, accessibleCompanies, companies, oosCommCenters]) => {
        if (user == null) {
          return [];
        }
        if (this.isAuthorizedForAllCompanies(user)) {
          return companies.valueSeq().toArray();
        }
        const authorizedCompanyIds = commCenters
          .map((cc) => cc.companyId)
          .concat([user.companyId])
          .concat(controlZones.map((cz) => cz.companyId))
          .concat(accessibleCompanies.map((ac) => ac.entityId))
          .concat(oosCommCenters.map((cc) => cc.companyId));

        return companies
          .filter((c) => authorizedCompanyIds.includes(c.id))
          .valueSeq()
          .toArray();
      }),
    );

    authorizedCompanies$.pipe(takeUntil(this._onDestroy$)).subscribe(this._authorizedCompanies$);
  }

  public observeCompanyAuthorized(companyId: string): Observable<boolean> {
    return this.authorizedCompanies$.pipe(
      map((acs) => acs.find((x) => x.id === companyId) != null),
    );
  }

  public ngOnDestroy(): void {
    this._onDestroy$.next(null);
    this._onDestroy$.complete();
  }
}
